import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { groupStageAPI } from '../../services/groupStageAPI';
import { tournamentAPI } from '../../services/api';
import './GroupStageDashboard.css';

export default function GroupStageDashboard() {
  const navigate = useNavigate();
  const { currentUser } = useApp();
  
  const [groupStages, setGroupStages] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [config, setConfig] = useState({
    numberOfGroups: 4,
    participantsPerGroup: 4,
    qualifiersPerGroup: 2,
    format: 'single_round_robin',
    winPoints: 3,
    drawPoints: 1,
    lossPoints: 0,
  });

  useEffect(() => {
    loadData();
  }, [currentUser]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get all tournaments for this organizer
      const tourRes = await tournamentAPI.getOrganizerSummary();
      setTournaments(tourRes.data.tournaments || []);

      // Get group stages for each tournament
      const gsData = [];
      for (const tour of (tourRes.data.tournaments || [])) {
        try {
          const gsRes = await groupStageAPI.getGroupStageForTournament(tour.id).catch(() => null);
          if (gsRes?.data) {
            gsData.push({
              ...gsRes.data,
              tournamentId: tour.id,
              tournamentName: tour.name,
              tournamentStatus: tour.status,
            });
          }
        } catch (e) {
          // Tournament doesn't have group stage, skip
        }
      }
      setGroupStages(gsData);
    } catch (err) {
      setError('Failed to load tournaments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroupStage = async (tournamentId) => {
    if (!tournamentId) {
      setError('Please select a tournament');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const result = await groupStageAPI.createGroupStage(tournamentId, config);
      
      // Reload data and navigate
      await loadData();
      navigate(`/organizer/tournament-group-stage/${tournamentId}`);
    } catch (err) {
      setError(err.message || 'Failed to create group stage');
    } finally {
      setCreating(false);
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: isNaN(value) ? value : parseInt(value, 10)
    }));
  };

  if (loading) {
    return (
      <div className="gs-dashboard">
        <div className="gs-loading">Loading group stages...</div>
      </div>
    );
  }

  return (
    <div className="gs-dashboard">
      {/* Header */}
      <div className="gs-header">
        <div>
          <h1 className="gs-title">GROUP STAGE TOURNAMENTS</h1>
          <p className="gs-subtitle">Manage all your group stage tournaments in one place</p>
        </div>
        <button 
          className="gs-btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          + New Group Stage
        </button>
      </div>

      {/* Stats */}
      <div className="gs-stats">
        <div className="gs-stat-card">
          <div className="gs-stat-label">Total Tournaments</div>
          <div className="gs-stat-value">{tournaments.length}</div>
        </div>
        <div className="gs-stat-card">
          <div className="gs-stat-label">Group Stages Active</div>
          <div className="gs-stat-value">{groupStages.filter(g => g.status === 'in_progress').length}</div>
        </div>
        <div className="gs-stat-card">
          <div className="gs-stat-label">Completed</div>
          <div className="gs-stat-value">{groupStages.filter(g => g.status === 'completed').length}</div>
        </div>
        <div className="gs-stat-card">
          <div className="gs-stat-label">Available for Setup</div>
          <div className="gs-stat-value">{tournaments.filter(t => !groupStages.find(g => g.tournamentId === t.id)).length}</div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="gs-error">
          <span>⚠️ {error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Group Stages List */}
      {groupStages.length === 0 ? (
        <div className="gs-empty">
          <div className="gs-empty-icon">🎾</div>
          <h2>No Group Stage Tournaments Yet</h2>
          <p>Create your first group stage tournament to get started</p>
          <button 
            className="gs-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            Create First Group Stage
          </button>
        </div>
      ) : (
        <div className="gs-grid">
          {groupStages.map(gs => (
            <div key={gs._id} className="gs-card">
              <div className="gs-card-header">
                <h3>{gs.tournamentName}</h3>
                <span className={`gs-badge gs-badge-${gs.status}`}>
                  {gs.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              <div className="gs-card-body">
                <div className="gs-card-row">
                  <span className="gs-label">Groups:</span>
                  <span className="gs-value">{gs.numberOfGroups}</span>
                </div>
                <div className="gs-card-row">
                  <span className="gs-label">Teams/Group:</span>
                  <span className="gs-value">{gs.participantsPerGroup}</span>
                </div>
                <div className="gs-card-row">
                  <span className="gs-label">Format:</span>
                  <span className="gs-value">{gs.format.replace('_', ' ')}</span>
                </div>
                <div className="gs-card-row">
                  <span className="gs-label">Qualifiers/Group:</span>
                  <span className="gs-value">{gs.qualifiersPerGroup}</span>
                </div>
                <div className="gs-card-row">
                  <span className="gs-label">Scoring:</span>
                  <span className="gs-value">{gs.winPoints}W-{gs.drawPoints}D-{gs.lossPoints}L</span>
                </div>
              </div>

              <div className="gs-card-footer" style={{display:'flex', gap:'8px'}}>
                <button 
                  className="gs-btn-primary"
                  onClick={() => navigate(`/organizer/group-manage/${gs.tournamentId}`)}
                >
                  Manage →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="gs-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="gs-modal" onClick={e => e.stopPropagation()}>
            <div className="gs-modal-header">
              <h2>Create New Group Stage</h2>
              <button className="gs-modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>

            <div className="gs-modal-body">
              {/* Tournament Selection */}
              <div className="gs-form-group">
                <label className="gs-form-label">Select Tournament *</label>
                <select 
                  className="gs-form-input"
                  value={selectedTournament || ''}
                  onChange={(e) => setSelectedTournament(e.target.value)}
                >
                  <option value="">-- Choose Tournament --</option>
                  {tournaments
                    .filter(t => !groupStages.find(g => g.tournamentId === t.id))
                    .map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.status})
                      </option>
                    ))}
                </select>
              </div>

              {/* Configuration */}
              <div className="gs-form-section">
                <h3>Tournament Structure</h3>

                <div className="gs-form-row">
                  <div className="gs-form-group">
                    <label className="gs-form-label">Number of Groups *</label>
                    <input 
                      type="number" 
                      className="gs-form-input"
                      min="2" max="16"
                      value={config.numberOfGroups}
                      onChange={(e) => handleConfigChange('numberOfGroups', e.target.value)}
                    />
                  </div>

                  <div className="gs-form-group">
                    <label className="gs-form-label">Teams per Group *</label>
                    <input 
                      type="number" 
                      className="gs-form-input"
                      min="2" max="64"
                      value={config.participantsPerGroup}
                      onChange={(e) => handleConfigChange('participantsPerGroup', e.target.value)}
                    />
                  </div>
                </div>

                <div className="gs-form-row">
                  <div className="gs-form-group">
                    <label className="gs-form-label">Qualifiers per Group *</label>
                    <input 
                      type="number" 
                      className="gs-form-input"
                      min="1" max="64"
                      value={config.qualifiersPerGroup}
                      onChange={(e) => handleConfigChange('qualifiersPerGroup', e.target.value)}
                    />
                  </div>

                  <div className="gs-form-group">
                    <label className="gs-form-label">Format</label>
                    <select 
                      className="gs-form-input"
                      value={config.format}
                      onChange={(e) => handleConfigChange('format', e.target.value)}
                    >
                      <option value="single_round_robin">Single Round Robin</option>
                      <option value="double_round_robin">Double Round Robin</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Scoring */}
              <div className="gs-form-section">
                <h3>Scoring Points</h3>

                <div className="gs-form-row">
                  <div className="gs-form-group">
                    <label className="gs-form-label">Win Points</label>
                    <input 
                      type="number" 
                      className="gs-form-input"
                      value={config.winPoints}
                      onChange={(e) => handleConfigChange('winPoints', e.target.value)}
                    />
                  </div>

                  <div className="gs-form-group">
                    <label className="gs-form-label">Draw Points</label>
                    <input 
                      type="number" 
                      className="gs-form-input"
                      value={config.drawPoints}
                      onChange={(e) => handleConfigChange('drawPoints', e.target.value)}
                    />
                  </div>

                  <div className="gs-form-group">
                    <label className="gs-form-label">Loss Points</label>
                    <input 
                      type="number" 
                      className="gs-form-input"
                      value={config.lossPoints}
                      onChange={(e) => handleConfigChange('lossPoints', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="gs-modal-footer">
              <button 
                className="gs-btn-secondary"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button 
                className="gs-btn-primary"
                onClick={() => handleCreateGroupStage(selectedTournament)}
                disabled={creating || !selectedTournament}
              >
                {creating ? 'Creating...' : 'Create Group Stage'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
